import React, { useState, useRef } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  FileText,
  Upload,
  X,
  GripVertical,
  Download,
  Loader2,
  Trash2,
  Plus,
  FileUp,
  Merge,
  Image,
  Search,
  User,
  FolderOpen,
} from 'lucide-react';
import api, { customersApi } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import type { Customer } from '../../types';

interface FileItem {
  id: string;
  file: File;
  name: string;
  size: number;
  isCustomerDoc?: boolean;
  customerName?: string;
  docType?: string;
}

interface CustomerDocument {
  type: string;
  label: string;
  url: string;
}

const DOCUMENT_LABELS: Record<string, string> = {
  photo: 'Pièce d\'identité',
  photo2: 'Pièce d\'identité 2',
  libre_passage: 'Libre passage',
  demande_suppletive: 'Demande supplétive',
  reponse: 'Réponse centrale',
  reponse_suppletive: 'Réponse supplétive',
  reponse_centrale: 'Réponse centrale (alt)',
  contrat_lemania: 'Contrat Lemania',
  procuration: 'Procuration',
};

const PdfMerger: React.FC = () => {
  const notify = useNotification();
  const [files, setFiles] = useState<FileItem[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Customer search state
  const [customerSearch, setCustomerSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState<string[]>([]);
  const customerSearchRef = useRef<HTMLDivElement>(null);

  // Get user role from auth context
  const { user } = useAuth();
  const userRole = user?.advisor?.role?.name?.toLowerCase() || '';
  const isAdmin = userRole === 'admin';

  // Customer search query
  const { data: customersData, isLoading: customersLoading } = useQuery({
    queryKey: ['customers-search', customerSearch],
    queryFn: () => customersApi.getAll({ search: customerSearch, per_page: 10 }),
    enabled: customerSearch.length >= 2 && isAdmin,
  });

  const mergeMutation = useMutation({
    mutationFn: async (filesToMerge: File[]) => {
      const formData = new FormData();
      filesToMerge.forEach((file, index) => {
        formData.append(`files[${index}]`, file);
      });
      const response = await api.post('/tools/merge-pdf', formData, {
        responseType: 'blob',
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: (blob) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `merged_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error: unknown) => {
      console.error('Merge error:', error);
      const errObj = error as { response?: { data?: { message?: string; file_name?: string; mime_type?: string; errors?: Record<string, string[]> } }; message?: string };
      const errorData = errObj.response?.data;
      let errorMessage = errorData?.message || errObj.message || 'Erreur inconnue';

      // Add file info if available
      if (errorData?.file_name) {
        errorMessage += ` | Fichier: ${errorData.file_name}`;
      }
      if (errorData?.mime_type) {
        errorMessage += ` | Type MIME: ${errorData.mime_type}`;
      }
      if (errorData?.errors) {
        const validationErrors = Object.values(errorData.errors).flat().join(', ');
        errorMessage += ` | ${validationErrors}`;
      }

      notify.error(`Erreur lors de la fusion: ${errorMessage}`);
    },
  });

  const allowedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/jpg',
    'image/png',
  ];

  const allowedExtensions = ['pdf', 'jpg', 'jpeg', 'png'];

  // Get available documents from a customer
  const getCustomerDocuments = (customer: Customer): CustomerDocument[] => {
    const docs: CustomerDocument[] = [];
    const docFields = ['photo', 'photo2', 'libre_passage', 'demande_suppletive', 'reponse', 'reponse_suppletive', 'reponse_centrale', 'contrat_lemania', 'procuration'];

    docFields.forEach((field) => {
      const value = customer[field as keyof Customer] as string;
      if (value && typeof value === 'string' && value.trim() !== '') {
        docs.push({
          type: field,
          label: DOCUMENT_LABELS[field] || field,
          url: value,
        });
      }
    });

    return docs;
  };

  // Validate file content (check magic bytes)
  const validateFileContent = async (blob: Blob, expectedType: string): Promise<boolean> => {
    const buffer = await blob.slice(0, 8).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // PDF magic bytes: %PDF (0x25 0x50 0x44 0x46)
    if (expectedType === 'pdf') {
      return bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46;
    }

    // JPEG magic bytes: FF D8 FF
    if (expectedType === 'jpg' || expectedType === 'jpeg') {
      return bytes[0] === 0xFF && bytes[1] === 0xD8 && bytes[2] === 0xFF;
    }

    // PNG magic bytes: 89 50 4E 47 0D 0A 1A 0A
    if (expectedType === 'png') {
      return bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47;
    }

    return true; // Allow unknown types
  };

  // Add customer document to file list
  const addCustomerDocument = async (doc: CustomerDocument, customer: Customer) => {
    const docKey = `${customer.uuid}-${doc.type}`;
    if (loadingDocuments.includes(docKey)) return;

    setLoadingDocuments((prev) => [...prev, docKey]);

    try {
      // Fetch the document via backend proxy (to handle files from lpp-api storage)
      // doc.url contains the storage path like "id_customers/UUID/file.pdf"
      const response = await api.post('/tools/fetch-document', { path: doc.url }, {
        responseType: 'blob',
      });

      const blob = response.data;

      // Determine extension and MIME type from URL
      const urlParts = doc.url.split('?')[0]; // Remove query params
      const extension = urlParts.split('.').pop()?.toLowerCase() || 'pdf';

      // Validate content matches expected type
      const isValid = await validateFileContent(blob, extension);
      if (!isValid) {
        throw new Error(`Le fichier n'est pas un ${extension.toUpperCase()} valide. Il est possible que le document ne soit plus disponible.`);
      }

      // Map extension to proper MIME type
      const mimeTypes: Record<string, string> = {
        pdf: 'application/pdf',
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
      };
      const mimeType = mimeTypes[extension] || blob.type || 'application/pdf';

      const filename = `${customer.fullname || customer.nom}_${doc.label}.${extension}`;

      // Create file with explicit MIME type
      const file = new File([blob], filename, { type: mimeType });

      const newFile: FileItem = {
        id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        file,
        name: filename,
        size: file.size,
        isCustomerDoc: true,
        customerName: customer.fullname || `${customer.prenom} ${customer.nom}`,
        docType: doc.label,
      };

      setFiles((prev) => [...prev, newFile]);
    } catch (error: unknown) {
      console.error('Error fetching document:', error);
      const errObj = error as { response?: { data?: { message?: string } }; message?: string };
      const errorMessage = errObj.response?.data?.message || errObj.message || 'Erreur inconnue';
      notify.error(`Erreur lors du chargement du document "${doc.label}": ${errorMessage}`);
    } finally {
      setLoadingDocuments((prev) => prev.filter((k) => k !== docKey));
    }
  };

  // Handle customer selection
  const handleSelectCustomer = async (customer: Customer) => {
    // Fetch full customer details to get documents
    try {
      const response = await customersApi.getDetails(customer.uuid);
      setSelectedCustomer(response.data.data);
    } catch {
      setSelectedCustomer(customer);
    }
    setShowCustomerDropdown(false);
    setCustomerSearch('');
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (customerSearchRef.current && !customerSearchRef.current.contains(event.target as Node)) {
        setShowCustomerDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isFileAllowed = (file: File) => {
    const extension = file.name.split('.').pop()?.toLowerCase() || '';
    return allowedTypes.includes(file.type) || allowedExtensions.includes(extension);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    const validFiles = selectedFiles.filter(isFileAllowed);

    if (validFiles.length !== selectedFiles.length) {
      notify.error('Seuls les fichiers PDF, JPG et PNG sont acceptés');
    }

    const newFiles: FileItem[] = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
    }));

    setFiles((prev) => [...prev, ...newFiles]);

    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    const validFiles = droppedFiles.filter(isFileAllowed);

    if (validFiles.length !== droppedFiles.length) {
      notify.error('Seuls les fichiers PDF, JPG et PNG sont acceptés');
    }

    const newFiles: FileItem[] = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      name: file.name,
      size: file.size,
    }));

    setFiles((prev) => [...prev, ...newFiles]);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const clearAllFiles = () => {
    setFiles([]);
  };

  // Drag and drop reordering
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragEnter = (index: number) => {
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newFiles = [...files];
      const [draggedItem] = newFiles.splice(draggedIndex, 1);
      newFiles.splice(dragOverIndex, 0, draggedItem);
      setFiles(newFiles);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleMerge = () => {
    if (files.length < 2) {
      notify.error('Veuillez sélectionner au moins 2 fichiers PDF');
      return;
    }
    mergeMutation.mutate(files.map((f) => f.file));
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center">
          <Merge className="w-7 h-7 mr-3 text-primary-600" />
          Fusion de PDF
        </h1>
        <p className="text-gray-600 mt-1">
          Sélectionnez et ordonnez vos fichiers PDF pour les fusionner en un seul document
        </p>
      </div>

      {/* Upload Area */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="bg-white rounded-xl shadow-sm border-2 border-dashed border-gray-300 hover:border-primary-400 transition-colors mb-6"
      >
        <div className="p-8 text-center">
          <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileUp className="w-8 h-8 text-primary-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Glissez-déposez vos fichiers ici
          </h3>
          <p className="text-sm text-gray-500 mb-4">ou</p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center px-5 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            <Plus className="w-5 h-5 mr-2" />
            Sélectionner des fichiers
          </button>
          <p className="text-xs text-gray-400 mt-4">
            Formats acceptés : PDF, JPG, PNG
          </p>
        </div>
      </div>

      {/* Customer Documents Section - Admin Only */}
      {isAdmin && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-6">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center">
              <FolderOpen className="w-5 h-5 text-gray-500 mr-2" />
              <span className="font-medium text-gray-900">Documents client</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Recherchez un client pour ajouter ses documents
            </p>
          </div>

          <div className="p-4">
            {/* Customer Search */}
            <div ref={customerSearchRef} className="relative mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher un client par nom..."
                  value={customerSearch}
                  onChange={(e) => {
                    setCustomerSearch(e.target.value);
                    setShowCustomerDropdown(e.target.value.length >= 2);
                  }}
                  onFocus={() => customerSearch.length >= 2 && setShowCustomerDropdown(true)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
                {customersLoading && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
              </div>

              {/* Dropdown */}
              {showCustomerDropdown && customersData?.data.data && customersData.data.data.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {customersData.data.data.map((customer) => (
                    <button
                      key={customer.uuid}
                      onClick={() => handleSelectCustomer(customer)}
                      className="w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center border-b border-gray-100 last:border-0"
                    >
                      <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                        <User className="w-4 h-4 text-primary-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {customer.fullname || `${customer.prenom} ${customer.nom}`}
                        </p>
                        <p className="text-xs text-gray-500 truncate">
                          {customer.fondation} • AVS: {customer.avs || 'N/A'}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {showCustomerDropdown && customerSearch.length >= 2 && !customersLoading && (!customersData?.data.data || customersData.data.data.length === 0) && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500 text-sm">
                  Aucun client trouvé
                </div>
              )}
            </div>

            {/* Selected Customer Documents */}
            {selectedCustomer && (
              <div className="border border-gray-200 rounded-lg">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between rounded-t-lg">
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center mr-3">
                      <User className="w-4 h-4 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {selectedCustomer.fullname || `${selectedCustomer.prenom} ${selectedCustomer.nom}`}
                      </p>
                      <p className="text-xs text-gray-500">
                        {selectedCustomer.fondation}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedCustomer(null)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded-lg"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-4">
                  {getCustomerDocuments(selectedCustomer).length > 0 ? (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                      {getCustomerDocuments(selectedCustomer).map((doc) => {
                        const docKey = `${selectedCustomer.uuid}-${doc.type}`;
                        const isLoading = loadingDocuments.includes(docKey);
                        const isAlreadyAdded = files.some(
                          (f) => f.isCustomerDoc && f.customerName === (selectedCustomer.fullname || `${selectedCustomer.prenom} ${selectedCustomer.nom}`) && f.docType === doc.label
                        );

                        return (
                          <button
                            key={doc.type}
                            onClick={() => !isAlreadyAdded && addCustomerDocument(doc, selectedCustomer)}
                            disabled={isLoading || isAlreadyAdded}
                            className={`flex items-center p-2.5 rounded-lg border text-left transition-colors ${
                              isAlreadyAdded
                                ? 'bg-green-50 border-green-200 text-green-700 cursor-default'
                                : isLoading
                                ? 'bg-gray-50 border-gray-200 cursor-wait'
                                : 'bg-white border-gray-200 hover:bg-primary-50 hover:border-primary-300 cursor-pointer'
                            }`}
                          >
                            {isLoading ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin text-gray-400" />
                            ) : isAlreadyAdded ? (
                              <FileText className="w-4 h-4 mr-2 text-green-600" />
                            ) : (
                              <Plus className="w-4 h-4 mr-2 text-gray-400" />
                            )}
                            <span className="text-xs font-medium truncate">{doc.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 text-center py-4">
                      Aucun document disponible pour ce client
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Files List */}
      {files.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <div className="flex items-center">
              <FileText className="w-5 h-5 text-gray-500 mr-2" />
              <span className="font-medium text-gray-900">
                {files.length} fichier{files.length > 1 ? 's' : ''} sélectionné{files.length > 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={clearAllFiles}
              className="text-sm text-red-600 hover:text-red-700 flex items-center"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Tout supprimer
            </button>
          </div>

          <div className="p-4">
            <p className="text-xs text-gray-500 mb-3 flex items-center">
              <GripVertical className="w-4 h-4 mr-1" />
              Glissez pour réorganiser l'ordre des fichiers
            </p>

            <div className="space-y-2">
              {files.map((file, index) => (
                <div
                  key={file.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnter={() => handleDragEnter(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  className={`flex items-center p-3 rounded-lg border transition-all cursor-move ${
                    draggedIndex === index
                      ? 'opacity-50 border-primary-300 bg-primary-50'
                      : dragOverIndex === index
                      ? 'border-primary-500 bg-primary-50'
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-gray-200 rounded-lg mr-3 text-gray-500">
                    <GripVertical className="w-5 h-5" />
                  </div>

                  {file.name.toLowerCase().endsWith('.pdf') ? (
                    <div className="w-8 h-8 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                      <FileText className="w-4 h-4 text-red-600" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <Image className="w-4 h-4 text-blue-600" />
                    </div>
                  )}

                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <div className="flex items-center gap-2">
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      {file.isCustomerDoc && (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-primary-100 text-primary-700">
                          <User className="w-3 h-3 mr-1" />
                          {file.customerName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-gray-400 bg-gray-200 px-2 py-1 rounded">
                      #{index + 1}
                    </span>
                    <button
                      onClick={() => removeFile(file.id)}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Merge Button */}
      {files.length >= 2 && (
        <div className="flex justify-center">
          <button
            onClick={handleMerge}
            disabled={mergeMutation.isPending}
            className="inline-flex items-center px-8 py-3.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all font-medium shadow-lg hover:shadow-xl"
          >
            {mergeMutation.isPending ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Fusion en cours...
              </>
            ) : (
              <>
                <Download className="w-5 h-5 mr-2" />
                Fusionner et télécharger
              </>
            )}
          </button>
        </div>
      )}

      {/* Help Text */}
      {files.length === 1 && (
        <div className="text-center text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <p className="text-sm">
            Ajoutez au moins un autre fichier PDF pour pouvoir effectuer la fusion
          </p>
        </div>
      )}

      {files.length === 0 && (
        <div className="text-center text-gray-500 py-8">
          <Upload className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <p className="text-sm">Aucun fichier sélectionné</p>
          <p className="text-xs text-gray-400 mt-1">
            Commencez par ajouter des fichiers PDF à fusionner
          </p>
        </div>
      )}
    </div>
  );
};

export default PdfMerger;
